import React, { useEffect, useState } from "react";
import { fetchReferralStatus } from "@/lib/api";
import { Gift, Sparkles } from "lucide-react";

/**
 * Small quiet progress bar on the Dashboard showing referral tally.
 * "1 of 3 friends have subscribed — 2 more for a free month"
 */
export default function ReferralProgress() {
  const [s, setS] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetchReferralStatus();
        setS(r);
      } catch (e) {
        console.error("referral status fetch failed", e);
      }
    })();
  }, []);

  if (!s) return null;
  const inCycle = s.conversions % s.threshold;
  const total = s.threshold;
  const rewards = s.rewards_earned || 0;
  const pending = s.pending_credits || 0;

  // Only show if user has ever gifted (conversions >= 0 doesn't help; show only when >= 1 or has rewards)
  if (s.conversions === 0 && rewards === 0 && pending === 0) return null;

  return (
    <div
      className="mb-12 border border-obsidian-600 bg-obsidian-800/50 p-5 md:p-6 flex items-center gap-5 animate-fade-up"
      data-testid="referral-progress"
    >
      <div className="h-9 w-9 border border-terracotta text-terracotta flex items-center justify-center shrink-0">
        {rewards > 0 ? <Sparkles className="h-4 w-4" /> : <Gift className="h-4 w-4" />}
      </div>
      <div className="flex-1">
        <p className="font-sans uppercase tracking-[0.24em] text-[0.65rem] text-terracotta mb-2">
          Referral · {s.conversions} friend{s.conversions === 1 ? "" : "s"} subscribed
        </p>
        <p className="font-serif italic text-base text-bone-200 leading-relaxed mb-3">
          {(() => {
            if (rewards > 0 && pending > 0) {
              return "A free month is waiting for you — it will apply on your next renewal.";
            }
            if (rewards > 0) {
              return "Your free month has been applied to the subscription. Thank you for spreading the workbook.";
            }
            const remaining = total - inCycle;
            return `${remaining} more subscriber${remaining === 1 ? "" : "s"} unlocks a free month for you.`;
          })()}
        </p>
        <div className="flex gap-1.5">
          {Array.from({ length: total }).map((_, i) => (
            <div
              key={`referral-tick-${i}`}
              className={`h-1 flex-1 max-w-16 ${i < inCycle ? "bg-terracotta" : "bg-obsidian-600"}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
